import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  FilledInput,
  FormGroup,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { TargetOrderUserHoldingsModal } from "./target-modals/TargetOrderUserHoldingsSelect";
import { TargetOrderFiatStableHoldingsModal } from "./target-modals/TargetOrderCurrencyModal";
import CachedIcon from "@mui/icons-material/Cached";
import api from "src/store/features/api";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  setTargetOrderType,
  setTargetTradeAmount,
  setTargetPrice,
  useTargetState,
  resetForm,
  resetFormField,
  toggleSummeryView,
  setTargetOrderBuy,
} from "src/store/features/TargetOrders.slice";
import { useAppDispatch } from "src/store/store";
import useInput from "src/utils/hooks/useInput";
import { TargetOrderStyles } from "../styles/TargetOrder.style";
import { formatCurrency, truncateTo4Decimals } from "src/utils/helpers/PriceAndUnitsHelpers";
import TargetOrderFormCompletion from "./form-control/TargetOrderFormCompletion";
import TargetOrderProjectionsBox from "./TargetOrderProjectionsBox";
import TargetOrderBuyTokenModal from "./target-modals/TargetOrderBuyTokens";
import TargetOrderFiatModal from "./target-modals/TargetOrderFiatCurrencysModal";
import clsx from "clsx";
import NumberFormatCustom from "../NumberField";
import { TokenPageDataRes } from "src/store/features/api/generated";
import useTargetOrderValidation, { removeFees } from "src/utils/hooks/useTargetOrderValidation";
import TargetOrderCloseModal from "./target-modals/TargetOrderCloseModal";
import { trackEvent } from "src/Amplitude";
import usePermission from "src/utils/hooks/usePermission";
import { TargetOrderVerificationBanner } from "./TagetOrderVerificationBanner";
import { divideNumbers } from "src/utils/SafeMaths";
import { useLocation } from "react-router-dom";

const TargetOrdersForm = () => {
  //----------------------Styles---------------------------
  const theme = useTheme();
  const classes = TargetOrderStyles();
  //----------------------REDUX-----------------------------
  const [marketPrice, setMarketPrice] = useState<TokenPageDataRes>();
  const { data: user } = api.useGetUserDatav2Query();
  const {
    orderType,
    selectedToken,
    selectedCurrency,
    formCompletion,
    viewSummery,
    tradeAmount,
    targetPrice,
  } = useTargetState();
  const [triggerTokenPrice, { data: selectedMarketPrice, isFetching: fetchingMarketPrice }] =
    api.useLazyGetCoinPageDataQuery();

  const dispatch = useAppDispatch();
  //----------------------CONST-----------------------------
  const userDefaultCurrency = user?.defaultCurrencyConfig.code;
  const BUY_TITLE = "I want to set a target order to " as const;
  const employee = user?.employee;
  // --------------------Hooks----------------
  const [completedForm, setCompletedForm] = useState(false);
  const [maxError, setMaxError] = useState("");
  const [disableButton, setDisableButton] = useState(true);
  const [closeToMarketModal, setCloseToMarketModal] = useState(false);
  const [codeParam, setCodeParam] = useState("");
  const { userPermission } = usePermission();
  const isGuest = userPermission === "guest";

  const location = useLocation();

  // --------------------Form Validation----------------

  /*
  - {useInput()} validates inputs in a token input box.
  - The `useInput` hook is used to handle input validation and update the form UI with error states, such as when inputs are zero or when the input is not valid.
  - Destructuring is used to extract specific values and methods from
  the returned object of the `useInput` hook, including the current input value,
  its validity, and error state, whether the input has been touched, if zero input and handlers for input blur and value changes.
*/

  const {
    reset: clearToken,
    reseted: resetterToken,
    value: tokenUnitValue,
    isValid: tokenUnitIsValid,
    hasError: tokenUnitError,
    isTouched: tokenIsTouched,
    zeroInput: tokenZeroInput,
    inputBlurHandler: tokenUnitBlurHandler,
    valueChangeHandler: tokenOnChange,
  } = useInput({
    setTargetTradeAmount,
    resetFormField,
    fieldIdentifier: "tokenValueInput",
  });

  const {
    reset: clearCurrency,
    reseted: resetterCurrency,
    value: currencyValue,
    hasError: currencyUnitError,
    zeroInput: currencyZeroInput,
    inputBlurHandler: currencyUnitBlurHandler,
    isValid: currencyValueValid,
    isTouched: currencyIsTouched,
    valueChangeHandler: currencyOnChange,
  } = useInput();

  /**
   * Validates the target order.
   * It returns an object containing the following error flags:
   * - closeToMarketError: Whether the order is within 5% of the market price.
   * - outOfRangeError: Whether the order is greater than 300% for a sell or greater than -50% for a buy order.
   * - buyTargetInvalid: Whether the target price is higher than the current market price.
   * - sellTargetInvalid: Whether the target price is lower than the current market price.
   * - insufficientBalance: Whether the user does not have sufficient funds for the target order.
   *
   *  @param {string} orderType - "Buy" or "Sell" order
   *  @param {number} [currencyValue] - the value of the currency input
   *  @param {number} [tokenValue] - the value of the token input
   *  @param {CryptoPriceRes} [coinMarketPrice] - current market price for the selected token
   *  @param {number|string} [tokenUnitValue] - the value of the token unit input
   *  @param {boolean} [tokenField] - whether the token field has been touched
   *  @param {boolean} [tokenFieldTouched] - whether the token field has been touched
   *  @param {boolean} [currencyField] - whether the currency field has been touched
   *  @param {boolean} [currencyFieldIsTouched] - whether the currency field has been touched
   *  @param {boolean} [tokenZeroInput] - whether the token input is zero
   *  @param {boolean} [currencyZeroInput] - whether the currency input is zero
   */
  const {
    closeToMarketError,
    outOfRangeError,
    minSpend,
    buyTargetInvalid,
    sellTargetInValid,
    insufficientBalance,
  } = useTargetOrderValidation(
    orderType[0],
    employee,
    selectedToken.value,
    selectedCurrency.value,
    selectedMarketPrice,
    tokenUnitValue,
    tokenUnitIsValid,
    tokenIsTouched,
    currencyValueValid,
    currencyIsTouched,
    tokenZeroInput,
    currencyZeroInput,
    user?.defaultTradeFee,
  );

  // --------------------HANDLERS----------------

  // displays a warning pop up if target is within 5% of current market price
  const nextHandler = () => {
    setMaxError("");
    if (closeToMarketError.isClose) {
      trackEvent(
        `Button_Click`,
        {
          button: "Warning_Modal_Open",
        },
        "Target",
      );
      setCloseToMarketModal(true);
    } else {
      trackEvent(`Select_Target_Order_Next_Button`);
      dispatch(toggleSummeryView());
    }
  };

  // --------------------EFFECTS----------------
  // dispatch input values to redux only if they are valid
  useEffect(() => {
    if (tokenUnitError && tokenZeroInput) {
      dispatch(resetFormField("tokenValueInput"));
    }
    dispatch(setTargetPrice(currencyValue));
    // prevents user from returning a values resetting
    if (tradeAmount && !tokenIsTouched && !tokenUnitValue) {
      dispatch(setTargetTradeAmount(tradeAmount));
    }
    if (targetPrice && !currencyIsTouched && !currencyValue) {
      dispatch(setTargetPrice(targetPrice));
    }

    if (currencyUnitError) {
      dispatch(resetFormField("currencyValueInput"));
    }
  }, [tokenUnitValue, tokenUnitError, tokenZeroInput, currencyValue, currencyUnitError]);

  useEffect(() => {
    if (selectedCurrency.code || selectedToken.code) {
      triggerTokenPrice({
        token: selectedToken.code,
        baseCurrency: selectedCurrency.code,
      });
    }
  }, [selectedCurrency, selectedToken, orderType]);

  useEffect(() => {
    if (selectedMarketPrice) {
      setMarketPrice(selectedMarketPrice);
    }
  }, [selectedMarketPrice]);

  useEffect(() => {
    if (
      formCompletion.tokenValueInput &&
      formCompletion.currencyValueInput &&
      formCompletion.tokenInput &&
      formCompletion.currencyInput
    ) {
      setCompletedForm(true);
    } else {
      setCompletedForm(false);
    }
  }, [formCompletion]);

  const handleMax = useCallback(() => {
    trackEvent("Max_Button_Pressed", { userId: user?.id }, "Target");
    setMaxError("");
    const getNumberOfTokens = (pricePerToken?: number, totalCurrencyAvailable?: number) => {
      return divideNumbers(totalCurrencyAvailable!, targetPrice!);
    };

    if (orderType[0] === "Sell") {
      dispatch(setTargetTradeAmount(selectedToken.available));
    } else {
      if (marketPrice) {
        if (!selectedCurrency.selected || !targetPrice) {
          setMaxError("Please select a currency and input a target price, before selecting max");
          return;
        }

        const maxValue = getNumberOfTokens(+marketPrice?.price, selectedCurrency.available);
        const minusFee = removeFees(maxValue, user?.defaultTradeFee);
        dispatch(setTargetTradeAmount(minusFee));
      }
    }
  }, [
    dispatch,
    marketPrice,
    targetPrice,
    orderType,
    selectedToken.available,
    selectedCurrency.available,
  ]);

  // get buy/sell code from url params and auto select
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    if (code) {
      dispatch(setTargetOrderBuy());
      setCodeParam(code);
    }
  }, [location.search]);
  return (
    <>
      <TargetOrderCloseModal
        handleClose={setCloseToMarketModal}
        open={closeToMarketModal}
        price={selectedMarketPrice}
      />
      <Grid container>
        <FormGroup className={classes.formGroupContainer}>
          <Grid item className={classes.formContainer}>
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={0.5}
              sx={{
                cursor: "pointer",
              }}>
              <Box
                id="targets-clear-button"
                sx={{
                  display: "flex",
                  color: theme.palette.mode === "light" ? "#324E61" : "#90A4AE",
                  "&:hover": {
                    color: `${theme.palette.error.main} !important `,
                  },
                }}
                onClick={() => {
                  trackEvent(`Select_Target_Order_Clear_Button`);
                  dispatch(resetForm());
                  clearCurrency();
                  setMaxError("");
                  clearToken();
                  setMarketPrice(undefined);
                }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                  }}>
                  Clear
                </Typography>
                <CachedIcon className={classes.clearIcon} />
              </Box>
            </Stack>
          </Grid>

          <Grid item sx={{ width: "80%", alignSelf: "center" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography id="main-explainer" className={classes.formSubHeading}>
                {BUY_TITLE}
              </Typography>
              {/* ----------------Select BUY OR SELL----------------*/}

              <Button
                disableRipple
                variant="contained"
                id="buy-sell-btn"
                className={classes.dropDownBuySell}
                onClick={() => {
                  dispatch(setTargetOrderType());
                  setMaxError("");
                  dispatch(resetForm());
                  clearCurrency();
                  clearToken();
                  trackEvent(`Select_Target_Order_Toggle_Buy_OR_Sell`, {
                    Selected_Order_Type: orderType,
                  });
                  // decide if to reset form or not
                }}
                endIcon={<ExpandMoreIcon />}>
                {orderType[0]}
              </Button>
            </Stack>
          </Grid>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={0}
            className={classes.tokenRowContainer}>
            {/* ----------------- TOKEN UNITS INPUT ------------- */}
            <FilledInput
              type="standard"
              id="token-input"
              key={resetterToken}
              className={classes.tokenInput}
              value={tradeAmount}
              onBlur={tokenUnitBlurHandler}
              onChange={tokenOnChange}
              disableUnderline
              onClick={() => trackEvent("Select_Target_Order_Token_Units_Input_Field")}
              sx={{
                color: tokenUnitError ? theme.palette.error.main : theme.palette.primary.main,
                border: tokenUnitError ? 1 : 0,
                borderColor: theme.palette.error.main,
                flex: 2,
                "& .MuiInputBase-input": {
                  padding: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                },
                "&.Mui-focused": {
                  border: 1,
                  borderColor: theme.palette.primary.main,
                },
              }}
              inputComponent={NumberFormatCustom as any}
              startAdornment={
                selectedToken.selected &&
                marketPrice && (
                  <InputAdornment
                    id="targets-max-button"
                    sx={{
                      fontSize: "12px",
                      textTransform: "capitalize",
                      textDecoration: "underline",
                      cursor: "pointer",
                      lineHeight: 1.21,

                      "&:hover": {
                        color: theme.palette.primary.main,
                      },
                    }}
                    onClick={handleMax}
                    position="start">
                    <Typography sx={{ fontSize: "12px", color: theme.palette.text.secondary }}>
                      Max
                    </Typography>
                  </InputAdornment>
                )
              }
              endAdornment={
                <InputAdornment
                  sx={{
                    fontSize: "12px",
                    textTransform: "lowercase",
                    lineHeight: 1.21,
                  }}
                  position="end">
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: tradeAmount ? theme.palette.primary.main : "",
                    }}>
                    Units
                  </Typography>
                </InputAdornment>
              }
              inputProps={{
                height: "33px",
                width: "100%",
                min: "0",
              }}
            />

            {/* ----------------------------Token Select-----------------------*/}
            {orderType[0] === "Sell" ? (
              <TargetOrderUserHoldingsModal userDefaultCurrency={userDefaultCurrency} />
            ) : (
              <TargetOrderBuyTokenModal code={codeParam} />
            )}
            {!tokenZeroInput && tokenIsTouched && selectedToken.code && !maxError && (
              <Typography
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: "14px",
                  lineHeight: 1.21,
                  flexGrow: 1,
                  top: 48,
                  position: "absolute",
                  width: "80%",
                  paddingTop: "5px",
                  alignSelf: "center",
                }}>
                Please enter a value above zero
              </Typography>
            )}

            {maxError && (
              <Typography
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: "14px",
                  lineHeight: 1.21,
                  flexGrow: 1,
                  top: 48,
                  position: "absolute",
                  width: "80%",
                  paddingTop: "5px",
                  alignSelf: "center",
                }}>
                {maxError}
              </Typography>
            )}
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: "80%",
              paddingTop: "16px",
              alignSelf: "center",
            }}>
            {!selectedToken.selected && orderType[0] === "Sell" && (
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: theme.palette.mode === "light" ? "#BDC2C5" : "#788187",
                }}>
                Available balance:
              </Typography>
            )}
            {/* -------------- USER LOW TOKEN INSUFFICIENT BALANCE --------------  */}

            {selectedToken.selected && orderType[0] === "Sell" && (
              <Typography
                className={clsx(classes.availableTitle, {
                  [classes.insufficientBalance]:
                    selectedCurrency.selected && insufficientBalance && completedForm,
                  [classes.availableSelected]: !selectedCurrency.selected && !insufficientBalance,
                })}
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: theme.palette.text.primary,
                }}>
                Available balance:
              </Typography>
            )}

            {selectedToken.selected && orderType[0] === "Sell" && (
              <Typography
                className={clsx(classes.formSubHeading, {
                  [classes.insufficientBalance]: insufficientBalance && completedForm,
                })}>
                {truncateTo4Decimals(selectedToken.available) + " " + selectedToken.code}
              </Typography>
            )}
          </Stack>
          {/* ----------------User Selects Currency----------------*/}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            className={classes.tokenRowContainer}>
            <Typography className={classes.formSubHeading}>at</Typography>
            {/* ----------------- TARGET ORDER INPUT ------------- */}
            <FilledInput
              id="select-currency-input"
              className={classes.tokenInput}
              key={resetterCurrency}
              fullWidth
              defaultValue={targetPrice}
              disableUnderline
              onChange={currencyOnChange}
              onFocus={(event) => event.target.select()}
              onBlur={currencyUnitBlurHandler}
              onClick={() => trackEvent("Select_Target_Order_Currency_Price_Input_Field")}
              sx={{
                color: currencyUnitError ? theme.palette.error.main : theme.palette.primary.main,
                border: currencyUnitError ? 1 : 0,
                borderColor: theme.palette.error.main,
                paddingRight: "5px",
                flex: 2,
                "&.Mui-focused": {
                  border: 1,
                  borderColor: theme.palette.primary.main,
                },
                "& .MuiInputBase-input": {
                  padding: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                },
              }}
              inputComponent={NumberFormatCustom as any}
              inputProps={{
                height: "33px",
                width: "100%",
                min: "0",
              }}
              endAdornment={
                <InputAdornment
                  sx={{
                    fontSize: "12px",
                    textTransform: "lowercase",
                    lineHeight: 1.21,
                  }}
                  position="end">
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: targetPrice ? theme.palette.primary.main : "",
                    }}>
                    price
                  </Typography>
                </InputAdornment>
              }
            />
            {/* ----------------- FIAT  SELECT MODALS ------------- */}
            {orderType[0] === "Buy" ? (
              <TargetOrderFiatStableHoldingsModal />
            ) : (
              <TargetOrderFiatModal />
            )}
            {!currencyZeroInput && currencyIsTouched && selectedCurrency.code && (
              <Typography className={classes.zeroBalanceText}>
                Please enter a value above zero
              </Typography>
            )}
          </Stack>

          {/* -------------- Market Price Conditions --------------  */}

          {/* -------------- Market Price Untouched state--------------  */}
          {!marketPrice && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                width: "80%",
                paddingTop: "16px",
                alignSelf: "center",
              }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: theme.palette.mode === "light" ? "#BDC2C5" : "#788187",
                }}>
                Market Price:
              </Typography>
            </Stack>
          )}
          {marketPrice && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                width: "80%",
                paddingTop: "16px",
                alignSelf: "center",
              }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  fontSize: "14px",
                  color: theme.palette.text.primary,
                }}>
                Market Price:
              </Typography>

              <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                {fetchingMarketPrice ? (
                  <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
                ) : (
                  formatCurrency(
                    +marketPrice.price,
                    selectedCurrency.type === "Fiat" ? selectedCurrency.code : "USD",
                  )
                )}
              </Typography>
            </Stack>
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: "80%",
              paddingTop: "16px",
              alignSelf: "center",
            }}>
            {/* --------------  USER LOW CURRENCY INSUFFICIENT BALANCE --------------  */}

            {orderType[0] === "Buy" && (
              <Typography
                className={clsx(classes.availableTitle, {
                  [classes.insufficientBalance]:
                    selectedCurrency.selected && insufficientBalance && completedForm,
                  [classes.availableSelected]: !selectedCurrency.selected && !insufficientBalance,
                })}
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  fontSize: "14px",
                  color: theme.palette.text.primary,
                }}>
                Balance:
              </Typography>
            )}

            {orderType[0] === "Buy" && selectedCurrency.selected && (
              <Typography
                className={clsx(classes.formSubHeading, {
                  [classes.insufficientBalance]: insufficientBalance && completedForm,
                })}>
                {formatCurrency(
                  selectedCurrency.available || 0,
                  selectedCurrency.type === "Fiat" ? selectedCurrency.code : "USD",
                )}
              </Typography>
            )}
          </Stack>
          {!completedForm && !isGuest && <TargetOrderFormCompletion />}
          {isGuest && <TargetOrderVerificationBanner />}
          {/* ----------------- PROJECTION BOX HANDLES ALL USER INPUT ERROR AND CALCULATES THE DISTANCE------------- */}
          {completedForm && !viewSummery && !isGuest && (
            <TargetOrderProjectionsBox
              closeToMarketError={closeToMarketError}
              outOfRangeError={outOfRangeError}
              buyTargetInvalid={buyTargetInvalid}
              sellTargetInValid={sellTargetInValid}
              insufficientBalance={insufficientBalance}
              disableButton={setDisableButton}
              minSpend={minSpend}
              employee={employee}
            />
          )}
          {!isGuest && (
            <Grid item className={classes.formContainer} id="target-click-away">
              <Button
                disableRipple
                id="target-submit-button"
                fullWidth
                disabled={disableButton}
                onClick={nextHandler}
                className={clsx(classes.nextBtn, {
                  [classes.nextBtnError]: insufficientBalance && formCompletion,
                })}>
                {insufficientBalance ? "Insufficient Balance" : "Next"}
              </Button>
            </Grid>
          )}
        </FormGroup>
      </Grid>
    </>
  );
};

export default TargetOrdersForm;
